import { useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuthStore } from "@/stores/auth";
import authService from "@/services/auth";

export function useAuth() {
  const { user, isAuthenticated, isLoading, setUser, logout } = useAuthStore();
  const queryClient = useQueryClient();

  // Fetch session on mount
  const { data: session, isLoading: sessionLoading } = useQuery({
    queryKey: ["session"],
    queryFn: () => authService.getSession(),
    retry: false,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  // Sync session with store
  useEffect(() => {
    if (!sessionLoading) {
      if (session?.user) {
        setUser(session.user);
      } else {
        setUser(null);
      }
    }
  }, [session, sessionLoading, setUser]);

  // Sign in with Google mutation
  const signInMutation = useMutation({
    mutationFn: () => authService.signInWithGoogle(),
  });

  // Sign out mutation
  const signOutMutation = useMutation({
    mutationFn: () => authService.signOut(),
    onSuccess: () => {
      logout();
      queryClient.invalidateQueries({ queryKey: ["session"] });
    },
  });

  return {
    user,
    isAuthenticated,
    isLoading: isLoading || sessionLoading,
    signInWithGoogle: () => signInMutation.mutate(),
    isSigningIn: signInMutation.isPending,
    signOut: () => signOutMutation.mutate(),
    isSigningOut: signOutMutation.isPending,
  };
}

export default useAuth;
